import { FC, PropsWithChildren, useRef, useState } from 'react';
import styled from 'styled-components';

import { GenAiLoadingAnimation } from 'modules/ai/ui/animations/GenAiLoadingAnimation';
import { useTranslation } from 'modules/queryBuilder/hooks/useTranslation';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { Exit16 } from 'ui/icons/aurora/16px/Exit16';
import { ErrorStateEditError } from 'ui/illustrations/ErrorStateEditError';
import { Popover, PopoverAlignment, PopoverContent } from 'ui/popover';
import { body2Bold, caption, colors, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { NeutralBanner } from 'ui/banner';

export type AIQuerySummaryProps = {
  align?: PopoverAlignment;
  description: string;
  error?: string;
  fetchDescription: ({
    onValidQuery,
    onInvalidQuery,
  }: { onValidQuery?: () => void; onInvalidQuery?: () => void }) => void;
  isLoading?: boolean;
  isReadOnly?: boolean;
  onClosePopover?: () => void;
  onCommit: (description: string) => void;
  onValidQuery?: () => void; // additonal Valid Query callback
  onInvalidQuery?: () => void; // additonal Invalid Query callback
};

export const descriptionMaxLength = 255;

export const testIds = {
  closeBtn: 'aiqs-popover-close-btn',
  link: 'aiqs-popover-link',
  popoverContent: 'aiqs-popover-content',
  trigger: 'aiqs-trigger',
};

const TitleContainer = styled.div`
  align-items: flex-start;
  column-gap: ${spacing.half};
  display: flex;
  > div:first-child {
    flex: 1 1 auto;
    margin-top: ${spacing.quarter};
  }
  > div:last-child {
    flex: 0 0 auto;
  }
`;

const StyledGQLError = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  text-align: center;
  width: ${pxToRem(296)}
`;

const StyledGQLErrorAck = styled.div`
  ${body2Bold};
`;

const StyledGQLErrorMsg = styled.div`
  ${caption};
  color: ${colors.textSecondary};
`;

const StyledLinkButton = styled(Button)`
  margin-top: ${spacing.half};
  width: 100%;
`;

// container necessary to allow for overflow of enlarged spakles
const AILoadingContainer = styled.div`
  align-items: center;
  display: flex;
  height: 42px;
  justify-content: center;
  margin: 4px 0;
  > div {
    margin-bottom: 0;
    transform: scale(0.5);
    transform-origin: center;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

export const AIQuerySummary: FC<PropsWithChildren<AIQuerySummaryProps>> = ({
  align = PopoverAlignment.Right,
  children,
  description = '',
  error = '',
  fetchDescription,
  isLoading = false,
  isReadOnly = false,
  onClosePopover,
  onCommit,
  onInvalidQuery,
  onValidQuery,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const ref = useRef(null);

  const onClickLink = () => {
    onCommit(description.trim().substring(0, descriptionMaxLength));
    setIsOpen(false);
  };

  const isDescriptionTruncated = description.slice(-3) === '...';

  const onOpenChange = (nextIsOpen: boolean) => {
    if (nextIsOpen) {
      fetchDescription({
        onValidQuery: () => {
          onValidQuery?.();
          setIsOpen(true);
        },
        onInvalidQuery,
      });
    } else {
      onClosePopover?.();
      setIsOpen(false);
    }
  };

  const TitleContent = () => {
    return (
      <TitleContainer>
        <div>{t('storybook.ai.summary.title')}</div>
        <div>
          <IconButton
            aria-label={t('storybook.ai.summary.title')}
            data-test={testIds.closeBtn}
            onClick={() => onOpenChange(false)}
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.GHOST}
          >
            <Exit16 />
          </IconButton>
        </div>
      </TitleContainer>
    );
  };

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <div ref={ref}>
        <PopoverContent
          container={ref.current}
          LinkContent={
            !isLoading &&
            !isReadOnly &&
            !!description &&
            !error && (
              <StyledLinkButton
                data-test={testIds.link}
                onClick={onClickLink}
                variant={BUTTON_VARIANTS.SECONDARY}
                size={BUTTON_SIZES.MEDIUM}
              >
                {t('storybook.ai.summary.link')}
              </StyledLinkButton>
            )
          }
          TitleContent={<TitleContent />}
          align={align}
          data-test={testIds.popoverContent}
          showArrow={false}
          side="bottom"
        >
          {isLoading ? (
            <AILoadingContainer>
              <GenAiLoadingAnimation />
            </AILoadingContainer>
          ) : (
            <>
              {error ? (
                <StyledGQLError>
                  <ErrorStateEditError />
                  <StyledGQLErrorAck>{t('errorMessages.hitSnag')}</StyledGQLErrorAck>
                  <StyledGQLErrorMsg>{error}</StyledGQLErrorMsg>
                </StyledGQLError>
              ) : (
                <ContentContainer>
                  {description}
                  {isDescriptionTruncated && (
                    <NeutralBanner>{t('storybook.ai.summary.banner')}</NeutralBanner>
                  )}
                </ContentContainer>
              )}
            </>
          )}
        </PopoverContent>
        {children}
      </div>
    </Popover>
  );
};
